import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { applePayApi } from '@api';
import native from '@native';
import { IPackage, IPackagePrice } from '@root/types/package';
import { useUnBindDevicesAction } from '../useUnBindDevices';
import { IOrderCreateData } from '@root/types/order';
import { useUserId } from '../useUser';
import Modal from '@root/components/modal';
import Toast from '@root/components/toast';
import { useCurrentHouseId } from '../useHouseList';
import { getOrderOriginal } from '@root/shared/referrerBuried';
import { getQueryString } from '@root/shared';

interface ICreateOrderProps {
  packageInfo: IPackage;
  priceInfo: IPackagePrice;
  deviceIds: string[];
}

const useCreateOrder = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userId = useUserId();
  const homeId = useCurrentHouseId();
  const { getUnBindDevices } = useUnBindDevicesAction();
  const [paying, setPaying] = useState(false);
  const originalRemark = getQueryString('sensor') || '';

  const createNativePay = useCallback(
    async ({
      orderId,
      priceInfo,
      packageInfo,
    }: {
      orderId: string;
      priceInfo: IPackagePrice;
      packageInfo: IPackage;
    }) => {
      // 发起支付
      try {
        const _data = {
          productId:
            priceInfo.appleGoodsId ||
            `${packageInfo.goodsId}_${priceInfo.planUnit}_${priceInfo.buyNum}`,
          productName: packageInfo.goodsName || '',
          userId,
          orderId,
          orderPrice: priceInfo.price || '',
          orderSourceType: 1,
          original: getOrderOriginal(),
        };

        console.log('native.payment.createApplePay', _data);

        const res = await native.payment.createApplePay(_data);

        Toast.destroy();

        console.log('native.payment.createApplePay', res, orderId);
        const code: number = res?.code || 0;
        setPaying(false);
        if ([200, 201].includes(code)) {
          navigate('/paySuccess', { replace: true });
        } else {
          Modal.alert({
            content:
              code === 211
                ? t('network_failed_try_again')
                : t('pay_failed_try_again'),
            // content: getErrorMsg(code),
            confirm: t('ok'),
          });
        }
      } catch (error) {
        setPaying(false);
        Toast.destroy();
        Toast.info(t('pay_failed_try_again'));
      }
    },
    [userId, navigate, t],
  );

  // 没有可选设备时默认订阅一份
  const onCreateOrder = useCallback(
    ({ packageInfo, priceInfo, deviceIds }: ICreateOrderProps) => {
      setPaying(true);

      Toast.info(t('create_order'), 0);

      const price = priceInfo.price ? Number(priceInfo.price).toFixed(2) : '';

      const _data: IOrderCreateData = {
        discountRate: 0,
        currency: priceInfo.currency,
        homeId,
        deviceIds: deviceIds.length ? deviceIds : undefined,
        // packageId: curPackage.goodsId,
        goodsReqs: [
          {
            goodsId: packageInfo.goodsId,
            goodsNum: priceInfo.buyNum || 1,
            goodsType: packageInfo.typeId || 5,
            goodsPriceId: priceInfo.goodsPriceId,
          },
        ],
        orderPrice: price,
        payPrice: price,
        preferentialPrice: 0,
        originalRemark,
      };

      applePayApi
        .createOrder(_data)
        .then((data: any) => {
          createNativePay({ priceInfo, orderId: data, packageInfo });
          if (deviceIds?.length) {
            getUnBindDevices();
          }
        })
        .catch((reason) => {
          Toast.destroy();
          setPaying(false);
          const status = reason.response?.status;
          const data = reason.response?.data || {};
          let msg = t('create_order_error');
          if (status === 400 && data.desc) {
            msg = data.desc;
          }
          Toast.info(msg);
        });
    },
    [createNativePay, getUnBindDevices, homeId, originalRemark, t],
  );

  useEffect(() => {
    return () => {
      Toast.destroy();
      Modal.close();
    };
  }, []);

  return {
    paying,
    onCreateOrder,
  };
};

export default useCreateOrder;
